// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // These helpers call this template's own server-side routes under `/api/...`.
  // In the browser, the correct base URL is the current origin (same host + protocol),
  // which avoids CORS issues if REACT_APP_MARKETPLACE_ROOT_URL is misconfigured.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or REACT_APP_MARKETPLACE_ROOT_URL.
  // When server-rendering (SSR), `window` is not available so we must rely on env values.
  const envRootUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  const resolvedRootUrl = marketplaceRootURL || envRootUrl;
  if (resolvedRootUrl) {
    return resolvedRootUrl.replace(/\/$/, '');
  }

  // SSR fallback for local environments
  const ssrPort = process.env.PORT || 3000;
  return `http://localhost:${ssrPort}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;
  const fetchImpl = typeof window !== 'undefined' ? window.fetch : globalThis.fetch;
  const hasBody = body !== undefined && body !== null;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && hasBody;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};
  const headersMaybe = headers
    ? { headers }
    : hasBody
    ? { headers: { 'Content-Type': 'application/transit+json' } }
    : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Only advertise Transit when a request actually contains a Transit body.
    // Safari otherwise sends bodyless DELETE requests as zero-length Transit payloads.
    ...headersMaybe,
    ...bodyMaybe,
    ...rest,
  };

  return fetchImpl(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

export const confirmStock = body => {
  return post('/api/confirm-stock', body);
};

export const getShippingRates = body => {
  return post('/api/shipping/rates', body);
};

export const createShipment = body => {
  return post('/api/shipping/shipments', body);
};

export const getShipmentLabel = body => {
  return post('/api/shipping/labels', body);
};

export const validateAddress = body => {
  return post('/api/shipping/validate-address', body);
};

export const onContactUs = body => {
  return post('/api/contact-us', body);
};

export const createShipmentLabelDownloadLink = body => {
  return post('/api/shipping//labels/download-link', body);
};

export const markTransactionSeen = body => {
  return post('/api/mark-transaction-seen', body);
};

export const recordMessageSent = body => {
  return post('/api/record-message-sent', body);
};

export const adminUpdateListing = body => {
  return post('/api/admin-update-listing', body);
};

export const fetchListingBids = listingId => {
  // Add cache-busting param to avoid conditional requests (304) breaking transit deserialization.
  const url = `${apiBaseUrl()}/api/listing-bids?listingId=${listingId}&_=${Date.now()}`;
  const fetchImpl = typeof window !== 'undefined' ? window.fetch : globalThis.fetch;
  return fetchImpl(url, {
      credentials: 'include',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    })
    .then(async res => {
      const text = await res.text();

      if (!res.ok) {
        throw new Error(`fetchListingBids failed: ${res.status} ${res.statusText}${text ? ` - ${text}` : ''}`);
      }

      if (!text) {
        throw new Error('fetchListingBids failed: empty response body');
      }

      return deserialize(text);
    });
};

export const acceptBidApi = body => {
  return post('/api/accept-bid', body);
};

export const placeBidApi = body => {
  return post('/api/place-bid', body);
};

export const updateBidApi = body => {
  return post('/api/update-bid', body);
};

export const recordListingView = body => {
  return post('/api/listing-view', body);
};

/** Fetch whether the current user has saved a listing. */
export const fetchFavoriteStatus = listingId => {
  return request(`/api/favorites?listingId=${encodeURIComponent(listingId)}`, {
    method: methods.GET,
    cache: 'no-store',
  });
};

/** Fetch one newest-first page of the current user's favorites. */
export const fetchFavorites = (page = 1) => {
  return request(`/api/favorites?page=${encodeURIComponent(page)}`, {
    method: methods.GET,
    cache: 'no-store',
  });
};

/** Fetch the current user's total number of favorites. */
export const fetchFavoriteCount = () => {
  return request('/api/favorites/count', {
    method: methods.GET,
    cache: 'no-store',
  });
};

/** Save a listing for the current user. */
export const addFavorite = (listingId, language) => {
  return post('/api/favorites', { listingId, language });
};

/** Remove a listing from the current user's favorites. */
export const removeFavorite = listingId => {
  return request(`/api/favorites/${encodeURIComponent(listingId)}`, {
    method: methods.DELETE,
  });
};

/** Create or reactivate a saved-search alert. */
export const saveSearchAlert = data => post('/api/saved-searches', data);

/** Log one rendered zero-result search. Duplicate event IDs are idempotent. */
export const createSearchMiss = data => post('/api/search-misses', data);

/** Record the first outcome for a rendered zero-result search. */
export const updateSearchMissOutcome = (eventId, outcome) =>
  request(`/api/search-misses/${encodeURIComponent(eventId)}/outcome`, {
    method: methods.PATCH,
    body: { outcome },
    keepalive: true,
  });

/** Fetch the current user's saved searches. */
export const fetchSavedSearches = (page = 1) =>
  request(`/api/saved-searches?page=${encodeURIComponent(page)}`, {
    method: methods.GET,
    cache: 'no-store',
  });

/** Remove a saved search owned by the current user. */
export const removeSavedSearch = id =>
  request(`/api/saved-searches/${encodeURIComponent(id)}`, { method: methods.DELETE });

/** Update one global email notification category. */
export const updateNotificationPreference = (category, enabled) =>
  post('/api/notification-preferences', { category, enabled });


export const onUpdateMetaDataTransaction = body => {
  return post('/api/updateMetaDataTransaction', body);
};

export const fetchLiveBidsListings = (params = {}) => {
  const { page, perPage, includeAll } = params;
  const pageQuery = page ? `&page=${page}` : '';
  const perPageQuery = perPage ? `&perPage=${perPage}` : '';
  const includeAllQuery = includeAll ? '&includeAll=true' : '';
  // Add cache-busting param to avoid conditional requests (304) breaking transit deserialization.
  const url =
    `${apiBaseUrl()}/api/listings/live-bids?_=${Date.now()}` +
    `${pageQuery}${perPageQuery}${includeAllQuery}`;
  const fetchImpl = typeof window !== 'undefined' ? window.fetch : globalThis.fetch;
  return fetchImpl(url, {
    credentials: 'include',
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
    },
  })
    .then(async res => {
      const text = await res.text();

      if (!res.ok) {
        throw new Error(`fetchLiveBidsListings failed: ${res.status} ${res.statusText}${text ? ` - ${text}` : ''}`);
      }

      if (!text) {
        throw new Error('fetchLiveBidsListings failed: empty response body');
      }

      return JSON.parse(text);
    });
};
